import { Injectable } from "@angular/core";
import { DataFile } from "libs/curation-service/src/lib/curation-service.types";

export enum FieldType {
    STRING = "string",        // Textual or string data
    NUMBER = "number",        // Numeric data of any kind, including decimals
    INTEGER = "integer",      // Whole number data
    BOOLEAN = "boolean",      // True or false value
    OBJECT = "object",        // Complex data structure, typically a JSON object
    ARRAY = "array",          // List or array of values
    DATE = "date",            // Calendar date value
    TIME = "time",            // Calendar date value
    DATETIME = "datetime",    // Combined date and time value
    YEAR = "year",            // Year value, typically a four-digit number
    YEAR_MONTH = "yearmonth", // Year and month value, typically in YYYY-MM format
    DURATION = "duration",    // A time duration (e.g. P1Y2M10DT2H30M) – ISO 8601 duration format
    GEOPOINT = "geopoint",    // Geographic point represented by latitude and longitude
    GEO_JSON = "geojson",     // Geographic data in GeoJSON format
    URL = "url",              // A Uniform Resource Locator (URL) for linking to web resources
    ANY = "any"               // Arbitrary data (least restrictive; fallback if inference is unclear)
}

export enum FieldRole {
  PRIMARY_IDENTIFIER = "primary identifier",  // Uniquely identifies a row within the table
  IDENTIFIER = "identifier",                  // Identifier for an entity associated with the record
  CHARACTERISTIC = "characteristic",          // Intrinsic property of the sample or source
  MEASURE = "measure",                        // Measurement output collected from an assay
  PROCEDURAL = "procedural",                  // Metadata about procedural aspects of the assay
  EXCLUDED = "excluded" ,                     // Field that is ignored because it is not relevant for analysis
  SERIES_CONTEXT ="series context",            // Contextual information about a series of related measurements (eg timepoint or dose for a longitudinal or dose response series)
  SERIES_MEASURE = "series measure",          // Measurement output collected from an assay within a series context
}

export enum FieldRelationType {
  SOURCE_OF = "source of",  // Source of sample or calculated measurement value
  CONTEXT_FOR_MEASURE = "context for measure",  // Contextual information about a measurement (eg timepoint or dose)
}


export interface DataSchema {
  id?: number;
  name: string;
  description?: string;
  field_descriptors?: FieldDescriptor[];
}

export interface FileType {
  id: number;
  data_schema?: DataSchema;
  data_schema_id?: number;
  path: string;
  name: string;
  description: string;
  encoding: string;
  format: string;
  hashing: string;
  compression: string;
  scheme: string;
  missing_values: string;
  measurement_type_ids?: number[];
}

export interface FieldDescriptor {
  id?: number;
  ref?: string; // request scoped reference id for new fields
  data_schema_id: number;
  name: string;
  title?: string | null;
  type?:  string | null;
  description?: string | null;
  rdf_type?: string | null;
  default?: string | null;
  missing_values?: string | null;
  true_values?: string | null;
  false_values?: string | null;
  field_role: string | FieldRole;
  unit?: string | null;
  timepoint?: string | null;
  timepoint_unit?: string | null;
  bare_number?: boolean | null;
  constraints: FieldConstraint;
  related_field?: FieldDescriptor;
  related_to?: string[] | null; // column names of related columns
  relation_type?: FieldRelationType | null;
  mapped_resource_id?: number | null;
  mapped_resource_ref?: string | null;
  context_label?: string | null;
  context_value?: string | null;
  context_quantity_kind?: string | null;
  context_unit?: string | null;
  column_order?: number | null;
}

export interface FieldConstraint {
  id?: number;
  required: boolean;
  unique: boolean;
  enum?: string;
  min_length?: number | null;
  max_length?: number | null;
  minimum?: string;
  maximum?: string;
  pattern?: string;
  format?: string;
  format_display?: string;
}


export interface PagedResponse {
  count: number;
  next: string;
  previous: string;
  results: unknown[]
}

export interface ValidationError {  
  cell: string;
  name: string;
  message: string;
  description: string;
  note: string;
  row_number: number;
  field_name: string;
  field_number: number;
  field_position: number;
}

export interface ValidationReport {
  validation_errors: ValidationError[];
  file: DataFile;
  file_type_name: string;
  date_created: Date;
  created_by?: string;
  path: string;
  errors: number;
  time: number;
  valid: boolean;
  fields: number;
  rows: number;
  match_count: number;
}


@Injectable({
    providedIn: 'root',
})

export class StructuredDataServiceTypes {

  booleanValues = [
    { true: "True", false: "False" },
    { true: "true", false: "false" },
    { true: "TRUE", false: "FALSE" },
    { true: "Yes", false: "No" },
    { true: "yes", false: "no" },
    { true: "YES", false: "NO" },
    { true: "1", false: "0" }
  ];
  
  formatTypes = [
    { type: "boolean", label: "Any", value: "any" },
    { type: "date", label: "Any", value: "any" },
    { type: "date", label: "MM/DD/YYYY (6/30/2024 or 06/30/2024)", value: "%m/%d/%Y" },
    { type: "date", label: "DD/MM/YYYY (30/6/2024 or 30/06/2024)", value: "%d/%m/%Y" },
    { type: "date", label: "YYYY-MM-DD (2024-6-30 or 2024-06-30)", value: "%Y-%m-%d" },
    { type: "date", label: "MM-DD-YYYY (6-30-2024 or 06-30-2024)", value: "%m-%d-%Y" },
    { type: "date", label: "DD-MM-YYYY (30-6-2024 or 30-06-2024)", value: "%d-%m-%Y" },
    { type: "date", label: "MM/DD/YY (6/30/24 or 06/30/24)", value: "%m/%d/%y" },
    { type: "date", label: "DD/MM/YY (30/6/24 or 30/06/24)", value: "%d/%m/%y" },
    { type: "date", label: "MM-DD-YY (6-30-24 or 06-30-24)", value: "%m-%d-%y" },
    { type: "date", label: "DD-MM-YY (30-6-24 or 30-06-24)", value: "%d-%m-%y" },
    { type: "number", label: "Numbers only", value: "true" },
    { type: "number", label: "Numbers and characters", value: "false" },
  ];


  getFormatTypes() {
    this.booleanValues.forEach((bool: any) => {
      const newFormat = {
        type: "boolean",
        label: `${bool.true}/${bool.false}`,
        value: `${bool.true},${bool.false}`
      };
      if (!this.formatTypes.some(format => 
        format.type === newFormat.type && 
        format.label === newFormat.label && 
        format.value === newFormat.value
      )) {
        this.formatTypes.push(newFormat);
      }
    });
    if (!this.formatTypes.some(format => format.type === "boolean" && format.value === "any")) {
      this.formatTypes.unshift({ type: "boolean", label: "Any", value: "any" });
    }
    return this.formatTypes;
  }
}